/**
 * A schedule as `/teams/{id}/schedules/V2` returns it, flattened into
 * `useGetScheduleFiltering`'s `filters` buckets.
 */
export interface ScheduleFilterEntry {
  id?: string
  name?: string
  type?: string
  /**
   * The team's roster for THIS schedule. Populated on the level the team is
   * actually registered in — typically the Division — and null on the parent
   * Season, which the team joins through its division rather than directly.
   */
  team_roster_id?: string | null
  parent?: { id?: string } | null
}

export interface ScheduleFilterBucket {
  name?: string
  schedules?: ScheduleFilterEntry[]
}

/**
 * The roster id for the selected season/division, read from data already in
 * memory.
 *
 * `/teams/{id}/schedules/V2` now returns `team_roster_id` on each schedule, so
 * the roster is known without a separate `/teams/{id}/rosters` request.
 *
 * The lookup deliberately checks the DIVISION first. `team_roster_id` sits on
 * the level the team registered in — usually the division — and is null on the
 * parent season, so resolving by season id alone would find nothing. When no
 * division is selected, any division whose `parent.id` is the chosen season
 * still identifies the same roster.
 *
 * Returns undefined when nothing matches (All Seasons, or a team not in the
 * selected competition), which callers treat as "no roster scope".
 */
export const resolveRosterIdFromFilters = (
  filters: ScheduleFilterBucket[] | null | undefined,
  scheduleId?: string | null,
  divisionId?: string | null
): string | undefined => {
  if (!filters?.length) {
    return undefined
  }

  const all = filters.flatMap((bucket) =>
    Array.isArray(bucket?.schedules) ? bucket.schedules : []
  )

  const rosterIdOf = (entry: ScheduleFilterEntry | undefined) =>
    entry?.team_roster_id || undefined

  // 1. An explicitly selected division is the most specific answer.
  if (divisionId) {
    const byDivision = rosterIdOf(all.find((entry) => entry?.id === divisionId))
    if (byDivision) {
      return byDivision
    }
  }

  if (!scheduleId) {
    return undefined
  }

  // 2. The season itself, for competitions where the team registers at that
  //    level and the id therefore carries the roster.
  const bySeason = rosterIdOf(all.find((entry) => entry?.id === scheduleId))
  if (bySeason) {
    return bySeason
  }

  // 3. Otherwise a child of that season — the common shape, where the roster
  //    hangs off the division and the season's own entry is null.
  return rosterIdOf(all.find((entry) => entry?.parent?.id === scheduleId))
}
